import { useEffect, useRef, useState } from "react";
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { useNavigate } from 'react-router-dom';
import StockService from '../../services/StockService';
import ProductService from '../../services/ProductService';
import WarehouseService from '../../services/WarehouseService';

const adjustmentTypes = [
    { label: 'Manuel Giriş', value: 6, direction: 1, icon: 'pi pi-plus-circle', desc: 'Sisteme doğrudan stok eklemesi yapın', color: '!bg-indigo-600', text: '!text-indigo-600' },
    { label: 'Manuel Çıkış', value: 7, direction: 2, icon: 'pi pi-minus-circle', desc: 'Sistemden doğrudan stok düşümü yapın', color: '!bg-rose-600', text: '!text-rose-600' },
    { label: 'Sayım Düzeltme', value: 8, direction: 1, icon: 'pi pi-pencil', desc: 'Fiziksel sayım sonrası farkları işleyin', color: '!bg-amber-500', text: '!text-amber-600' },
    { label: 'Fire / Zayi', value: 9, direction: 2, icon: 'pi pi-trash', desc: 'Bozulan veya kaybolan ürünleri düşürün', color: '!bg-slate-900', text: '!text-slate-900' }
];

export default function ManualStockAdjustmentPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(adjustmentTypes[0]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [formData, setFormData] = useState({
        productId: null,
        warehouseId: null,
        quantity: 0,
        direction: 1,
        movementType: 6,
        description: ''
    });

    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadLookups();
    }, []);

    const loadLookups = async () => {
        try {
            const warRes = await WarehouseService.getAll();
            // Controllers wrap responses in data object.
            // In WarehouseController.cs: ServiceResult<List<WarehouseListDto>>.SuccessResult(result)
            // Axios response is res.data.data
            const warData = warRes.data?.data || warRes.data;

            if (warData && Array.isArray(warData)) {
                setWarehouses(warData.map(w => ({ label: w.name, value: w.id, isDefault: w.isDefault })));
                const defaultWar = warData.find(w => w.isDefault) || warData[0];
                if (defaultWar) {
                    setFormData(prev => ({ ...prev, warehouseId: defaultWar.id }));
                }
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: 'Depo bilgileri yüklenemedi' });
        }
    };

    const searchProducts = async (event) => {
        if (event.query.trim().length < 3) {
            setFilteredProducts([]);
            return;
        }

        try {
            const res = await ProductService.getAutoComplete(event.query);
            // res is directly the list of products because ProductController returns Task<List<ProductAutoCompleteDto>>
            const productData = res.data?.data || res.data;

            if (productData && Array.isArray(productData)) {
                setFilteredProducts(productData.map(p => ({
                    id: p.id,
                    name: p.name,
                    barcode: p.barcode,
                    label: `${p.name} (${p.barcode || 'Barkodsuz'})`
                })));
            } else {
                setFilteredProducts([]);
            }
        } catch (error) {
            console.error('Ürün arama hatası:', error);
        }
    };

    const handleSave = async () => {
        if (!selectedProduct || !formData.warehouseId || formData.quantity <= 0) {
            toast.current.show({ severity: 'warn', summary: 'Eksik Bilgi', detail: 'Lütfen tüm zorunlu alanları doldurunuz.' });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                productId: selectedProduct.id,
                movementType: selectedType.value,
                direction: selectedType.direction || formData.direction
            };
            const res = await StockService.manualAdjustment(payload);
            const result = res.data;
            if (result && result.success) {
                toast.current.show({ severity: 'success', summary: 'Başarılı', detail: 'Stok hareketi başarıyla işlendi.' });
                setTimeout(() => navigate('/stock/movements'), 1200);
            } else {
                toast.current.show({ severity: 'error', summary: 'Hata', detail: result?.message || 'İşlem başarısız.' });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Hata', detail: 'İşlem sırasında bir hata oluştu.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2 md:p-6 bg-white border border-slate-100 rounded-[3rem] shadow-2xl shadow-indigo-400 animate-fade-in max-w-6xl mx-auto">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />

            <div className="flex flex-col md:flex-row items-center justify-between mb-12 gap-6">
                <div className="flex items-center gap-6">
                    <div className="w-20 h-20 rounded-[2.5rem] bg-slate-900 flex items-center justify-center shadow-2xl border-4 border-white">
                        <i className="pi pi-directions-alt text-white text-3xl" />
                    </div>
                    <div>
                        <h1 className="text-3xl md:text-4xl font-black text-slate-900 leading-none uppercase tracking-tighter">Stok Yönetim Paneli</h1>
                        <p className="text-slate-400 font-bold text-xs uppercase tracking-[0.2em] mt-3 italic">Hızlı Hareket ve Düzeltme Merkezi</p>
                    </div>
                </div>
                <Button label="HAREKET LİSTESİ" icon="pi pi-list" className="p-button-text font-black text-slate-500 hover:text-indigo-600 rounded-2xl" onClick={() => navigate('/stock/movements')} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

                {/* Type Selection Tiles */}
                <div className="lg:col-span-5 space-y-4">
                    <h2 className="text-[14px] font-black text-slate-800 uppercase tracking-[0.1em] mb-2 pl-2">İŞLEM TÜRÜ SEÇİNİZ</h2>
                    <div className="grid grid-cols-1 gap-4">
                        {adjustmentTypes.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => setSelectedType(type)}
                                className={`group relative p-6 rounded-[2.5rem] border-2 transition-all duration-300 text-left overflow-hidden ${selectedType.value === type.value ? '!border-slate-900 !bg-slate-900 text-white shadow-2xl shadow-slate-400 -translate-y-1' : 'border-slate-100 bg-white hover:border-slate-200'}`}
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 opacity-10 rounded-full -mr-16 -mt-16 blur-2xl ${type.color}`} />
                                <div className="flex items-center gap-6 relative z-10">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${selectedType.value === type.value ? 'bg-white/10 text-white' : `${type.color} text-white`}`}>
                                        <i className={`${type.icon} text-2xl`} />
                                    </div>
                                    <div>
                                        <h3 className={`font-black text-lg uppercase tracking-tight m-0 ${selectedType.value === type.value ? 'text-white' : 'text-slate-800'}`}>{type.label}</h3>
                                        <p className={`text-[11px] font-bold mt-1 m-0 ${selectedType.value === type.value ? 'text-slate-400' : 'text-slate-400'}`}>{type.desc}</p>
                                    </div>
                                    {selectedType.value === type.value && <i className="pi pi-check-circle ml-auto text-emerald-400 text-2xl animate-bounce-in" />}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Form Section */}
                <div className="lg:col-span-7">
                    <Card className="rounded-[3rem] border-none shadow-2xl shadow-indigo-400 h-full overflow-hidden">
                        <div className="p-2 md:p-4 space-y-8">
                            <div className="flex items-center justify-between">
                                <h2 className="text-xl font-black text-slate-800 m-0">İŞLEM DETAYLARI</h2>
                                <Tag value={selectedType.label} className={`${selectedType.color} text-white font-black px-4 py-2 rounded-xl`} />
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">ÜRÜN SEÇİMİ (MIN 3 HARF)</label>
                                    <AutoComplete
                                        value={selectedProduct}
                                        suggestions={filteredProducts}
                                        completeMethod={searchProducts}
                                        field="name"
                                        placeholder="Hangi ürün üzerinde işlem yapılacak?"
                                        className="w-full"
                                        inputClassName="w-full elite-input"
                                        onChange={(e) => setSelectedProduct(e.value)}
                                        itemTemplate={(item) => (
                                            <div className="flex flex-col py-1">
                                                <span className="font-bold text-slate-700">{item.name}</span>
                                                <span className="text-[10px] text-slate-400 font-black uppercase">{item.barcode || 'Barkodsuz'}</span>
                                            </div>
                                        )}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-1 gap-2">
                                    <div className="mb-4"> 
                                        <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">HEDEF DEPO</label>
                                        <Dropdown
                                            value={formData.warehouseId}
                                            options={warehouses}
                                            onChange={(e) => setFormData({ ...formData, warehouseId: e.value })}
                                            placeholder="Depo seçiniz"
                                            className="w-full elite-dropdown" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">MİKTAR</label>
                                        <InputNumber
                                            value={formData.quantity}
                                            onValueChange={(e) => setFormData({ ...formData, quantity: e.value })}
                                            min={0}
                                            placeholder="0.00"
                                            className="w-full"
                                            inputClassName="w-full p-3 bg-slate-50 border-slate-100 rounded-[1.5rem] font-black text-2xl text-slate-900 text-center"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">AÇIKLAMA / SİSTEM NOTU</label>
                                    <InputTextarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        rows={4}
                                        placeholder="Bu hareketle ilgili bir açıklama giriniz..."
                                        className="w-full p-5 bg-slate-50 border-slate-100 rounded-[1.5rem] font-medium text-slate-600 focus:bg-white transition-all"
                                    />
                                </div>
                            </div>

                            <div className="pt-8 flex flex-col gap-3">
                                <Button
                                    label="HAREKETİ ONAYLA VE KAYDET"
                                    icon="pi pi-check-circle"
                                    loading={loading}
                                    className="!bg-slate-900 border-none font-black text-sm h-[72px] rounded-[1.8rem] shadow-2xl transition-all hover:scale-[1.02] hover:bg-emerald-600"
                                    onClick={handleSave}
                                />
                                <p className="text-center text-[10px] text-slate-400 font-bold uppercase tracking-widest">BU İŞLEM GERİ ALINAMAZ VE STOK LOGLARINA KAYDEDİLİR</p>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>

            <style>{`
                .elite-input, .elite-dropdown {
                    background: #f8fafc !important;
                    border: 2px solid #f1f5f9 !important;
                    border-radius: 1.5rem !important;
                    padding: 0.8rem 1.2rem !important;
                    font-weight: 700;
                    color: #1e293b;
                }
                .elite-input:focus, .elite-dropdown:focus {
                    border-color: #6366f1 !important;
                    background: #fff !important;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
                }
                .p-autocomplete-panel {
                    border-radius: 1.5rem !important;
                    overflow: hidden !important;
                    margin-top: 8px !important;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
                }
                .p-dropdown-panel {
                    border-radius: 1.5rem !important;
                    overflow: hidden !important;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
                }
                .animate-bounce-in {
                    animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55);
                }
                @keyframes bounceIn {
                    0% { opacity: 0; transform: scale(0.3); }
                    50% { opacity: 1; transform: scale(1.05); }
                    70% { transform: scale(0.9); }
                    100% { transform: scale(1); }
                }
            `}</style>
        </div>
    );
}
